import { parseISO, isToday, isYesterday, format } from "date-fns";

import { NavigateFunction } from "react-router-dom";
import { currencyTypes } from "../types";
import { UserType } from "../utils-func/enums";

function getRandomColors(index: number) {
  const colors = ["#1F3A93", "#009688", "#1976D2", "#E65100", "#C62828"];
  return colors[index % colors.length];
}

const formatDate = (dateString: string) => {
  const date = parseISO(dateString);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEE MMM d, yyyy"); // Example: Tue Feb 18 2025
};

function getUploadDate(created_at: string): string {
  const createdDate = new Date(created_at);
  const now = new Date();

  const diffTime = now.getTime() - createdDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffMonths =
    now.getMonth() -
    createdDate.getMonth() +
    12 * (now.getFullYear() - createdDate.getFullYear());

  if (diffDays === 0) {
    return "Uploaded today";
  }
  if (diffDays === 1) {
    return "Uploaded 1 day ago";
  }
  if (diffDays < 30) {
    return `Uploaded ${diffDays} days ago`;
  }
  if (diffMonths === 1) {
    return "Uploaded 1 month ago";
  }
  return `Uploaded ${diffMonths} months ago`;
}

function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now.getTime() - 86400000); // 86400000 = 24 hours in milliseconds

  if (date.toLocaleDateString() === now.toLocaleDateString()) {
    return "Today";
  } else if (date.toLocaleDateString() === yesterday.toLocaleDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
}

function formatTimeElapsed(date: string): string {
  const now = new Date();
  const createdDate = new Date(date);
  const diffInSeconds = Math.floor(
    (now.getTime() - createdDate.getTime()) / 1000,
  );

  if (diffInSeconds < 60)
    return diffInSeconds === 1
      ? "1 second ago"
      : `${diffInSeconds} seconds ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60)
    return diffInMinutes === 1 ? "1 min ago" : `${diffInMinutes} mins ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7)
    return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return diffInWeeks === 1 ? "1 week ago" : `${diffInWeeks} weeks ago`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12)
    return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
  const diffInYears = Math.floor(diffInMonths / 12);
  return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
}

const handleNavigate = (
  role: string | undefined,
  navigate: NavigateFunction,
) => {
  switch (role) {
    case UserType.PRIVATE_INDIVIDUAL: {
      navigate("/private-individual/overview");

      break;
    }

    case UserType.ADMIN: {
      navigate("/admin/overview");
      break;
    }

    case UserType.POLICE: {
      navigate("/police/overview");
      break;
    }
    case UserType.BALIFF: {
      navigate("/baliff/overview");
      break;
    }
    case UserType.PRESIDENT: {
      navigate("/president/overview");
      break;
    }
    case UserType.LAWYER: {
      navigate("/lawyer/overview");
      break;
    }
    case UserType.REGISTRAR_1_PAYMENT: {
      navigate("/registrar1-payment/overview");
      break;
    }
    case UserType.REGISTRAR_1_ASSESSMENT: {
      navigate("/registrar1-assessment/overview");
      break;
    }
    case UserType.REGISTRAR_2: {
      navigate("/registrar2/overview");
      break;
    }
    case UserType.PANEL: {
      navigate("/panel/overview");
      break;
    }

    default:
      {
      }
      break;
  }
};

const formatCurrency = (currencyType: currencyTypes) => {
  return new Intl.NumberFormat(currencyType.iso, {
    style: "currency",
    currency: currencyType.slug,
  });
};

export {
  getRandomColors,
  getUploadDate,
  formatDate,
  formatTimestamp,
  formatTimeElapsed,
  handleNavigate,
  formatCurrency,
};
